import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { CoreInvitesContext, CoreInvitesSource } from '@confluence/core-invites-provider';
import { useInviteLink } from '@confluence/core-invites-provider/entry-points/useInviteLink';
import type { FlagsStateContainer, FlagDescriptor } from '@confluence/flags';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withTransparentErrorBoundary';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { withFlags } from '@confluence/flags';
import { useCopyToClipboard } from '@confluence/copy-content-link';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

import { FirstPublishNthUserShareFlag } from './FirstPublishNthUserShareFlag';

const messages = defineMessages({
	inviteFlagTitle: {
		id: 'contextual-invite-nudge-flag.title',
		defaultMessage: 'You’ve created your first page 🎉',
		description:
			'The title used in a flag to nudge the user to invite other users into confluence.',
	},
	inviteFlagTitleLiveDoc: {
		id: 'contextual-invite-nudge-flag.title.live-doc',
		defaultMessage: 'You’ve created your first live doc 🎉',
		description:
			'The title used in a flag to nudge the user to invite other users into confluence after creating a live doc.',
	},
	inviteFlagDescription: {
		id: 'contextual-invite-nudge-flag.description',
		defaultMessage: 'Add your teammates so they can collaborate with you.',
		description:
			'The description used in a flag to nudge the user to invite other users into confluence.',
	},
	inviteFlagAdminCTA: {
		id: 'contextual-invite-nudge-flag.adminCTA',
		defaultMessage: 'Copy shareable invite link',
		descrption:
			'The action of the flag to nudge the admin user to share the invite link to other users.',
	},
	inviteFlagAdminCopiedCTA: {
		id: 'contextual-invite-nudge-flag.adminCTA.copied',
		defaultMessage: 'Copied!',
		descrption:
			'The text to show indicate the admin that invite link to other users has been copied.',
	},
	inviteFlagNthUserCTA: {
		id: 'contextual-invite-nudge-flag.nthUserCTA',
		defaultMessage: 'Invite people',
		description:
			'The action of the flag to nudge the nth user to open the invite modal to invite other users.',
	},
});

const ADMIN_FLAG_ID = 'contextual-invite-nudge-admin-user-flag';
const END_USER_FLAG_ID = 'contextual-invite-nudge-end-user-flag';

// Constants for source values
export const LIVE_PAGE_SOURCE = 'live-page' as const;

/**
 * Source for analytics tracking. 'live-page' shows special copy when feature gate is enabled.
 */
export type ContextualInviteNudgeSource = 'live-page' | string;

// Determines if live doc title should be used based on source and feature gate
const shouldUseLiveDocTitle = (source: ContextualInviteNudgeSource): boolean => {
	return source === LIVE_PAGE_SOURCE && fg('cc_engagement_contextual_nudge_live_doc_title');
};

export interface ContextualInviteNudgeFlagBaseProps {
	flags: FlagsStateContainer;
	source: ContextualInviteNudgeSource;
	contentId: string;
	contentType: string;
	spaceKey?: string;
}

type ContextualInviteNudgeFlagProps = {
	flags: FlagsStateContainer;
	source: ContextualInviteNudgeSource;
};

export const ContextualInviteNudgeFlag = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(
	withFlags(
		({ flags, source, contentId, contentType, spaceKey }: ContextualInviteNudgeFlagBaseProps) => {
			const { isSiteAdmin } = useContext(SPAViewContext);
			const {
				data: onboardingStateData,
				loading: onboardingStateLoading,
				error: onboardingStateError,
			} = useGetOnboardingState([ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN]);

			const { isFoundingAdmin } = deserializeState(onboardingStateData) || {};

			if (onboardingStateLoading) {
				return null;
			}

			if (spaceKey && !onboardingStateError && !isFoundingAdmin) {
				expVal('cc_activation_first_publish_share_nudge_aa', 'cohort', 'not-enrolled');
				if (expValEquals('cc_activation_first_publish_share_nudge', 'cohort', 'experiment')) {
					return (
						<FirstPublishNthUserShareFlag
							flags={flags}
							source={source}
							contentId={contentId}
							contentType={contentType}
							spaceKey={spaceKey}
						/>
					);
				}
			}

			if (onboardingStateError) {
				markErrorAsHandled(onboardingStateError);
			}

			if (isSiteAdmin) {
				return (
					<>
						{onboardingStateError && <ErrorDisplay error={onboardingStateError} />}
						<AdminFlag flags={flags} source={source} />
					</>
				);
			}

			return (
				<>
					{onboardingStateError && <ErrorDisplay error={onboardingStateError} />}
					<EndUserFlag flags={flags} source={source} />
				</>
			);
		},
	),
);

const TIMEOUT_INTERVAL = 2000;

const AdminFlag = ({ flags, source }: ContextualInviteNudgeFlagProps) => {
	const [isInviteLinkCopied, setIsInviteLinkCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>();
	const { inviteLink, error, loading } = useInviteLink({ allowGenerateNewLink: true });
	const { copyToClipboard } = useCopyToClipboard();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'adminContextualInviteNudgeFlag',
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	useEffect(() => {
		if (isInviteLinkCopied) {
			timer.current = setTimeout(() => {
				setIsInviteLinkCopied(false);
			}, TIMEOUT_INTERVAL);
		} else {
			clearInterval(timer.current);
		}

		return () => clearInterval(timer.current);
	}, [isInviteLinkCopied, setIsInviteLinkCopied, timer]);

	const onCopy = useCallback(() => {
		void copyToClipboard(inviteLink).then(() => {
			setIsInviteLinkCopied(true);
		});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'adminContextualInviteNudgeFlagCopyClick',
				source,
			},
		}).fire();
	}, [copyToClipboard, inviteLink, createAnalyticsEvent, source]);

	const flagDescriptor: FlagDescriptor = useMemo(() => {
		return {
			id: ADMIN_FLAG_ID,
			type: 'success-circle',
			messageId: ADMIN_FLAG_ID,
			messageType: 'engagement',
			title: (
				<FormattedMessage
					{...(shouldUseLiveDocTitle(source)
						? messages.inviteFlagTitleLiveDoc
						: messages.inviteFlagTitle)}
				/>
			),
			description: <FormattedMessage {...messages.inviteFlagDescription} />,
			actions: [
				isInviteLinkCopied
					? {
							content: <FormattedMessage {...messages.inviteFlagAdminCopiedCTA} />,
						}
					: {
							content: <FormattedMessage {...messages.inviteFlagAdminCTA} />,
							onClick: onCopy,
						},
			],
			close: 'never',
			onClose: () => {
				void flags.hideFlag(ADMIN_FLAG_ID);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'adminContextualInviteNudgeFlagDismissClick',
						source,
					},
				}).fire();
			},
		};
	}, [isInviteLinkCopied, onCopy, flags, source, createAnalyticsEvent]);

	useEffect(() => {
		if (!loading && !error && inviteLink) {
			void flags.showFlag(flagDescriptor);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, error, inviteLink, isInviteLinkCopied]);

	if (loading) {
		return null;
	}

	if (error) {
		// Known error, mark as handled and return nothing
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
			return null;
		}

		// Unknown error, show error display and report to monitoring client
		return <ErrorDisplay error={error} />;
	}

	return null;
};

const EndUserFlag = ({ flags, source }: ContextualInviteNudgeFlagProps) => {
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'endUserContextualInviteNudgeFlag',
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const flagDescriptor: FlagDescriptor = useMemo(() => {
		return {
			id: END_USER_FLAG_ID,
			type: 'success-circle',
			messageId: END_USER_FLAG_ID,
			messageType: 'engagement',
			title: (
				<FormattedMessage
					{...(shouldUseLiveDocTitle(source)
						? messages.inviteFlagTitleLiveDoc
						: messages.inviteFlagTitle)}
				/>
			),
			description: <FormattedMessage {...messages.inviteFlagDescription} />,
			actions: [
				{
					content: <FormattedMessage {...messages.inviteFlagNthUserCTA} />,
					onClick: () => {
						openCoreInvites?.(CoreInvitesSource.CONTEXTUAL_INVITE_NUDGE);
						void flags.hideFlag(END_USER_FLAG_ID);
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'endUserContextualInviteNudgeFlagInviteClick',
								source,
							},
						}).fire();
					},
				},
			],
			close: 'never',
			onClose: () => {
				void flags.hideFlag(END_USER_FLAG_ID);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'endUserContextualInviteNudgeFlagDismissClick',
						source,
					},
				}).fire();
			},
		};
	}, [flags, openCoreInvites, source, createAnalyticsEvent]);

	useEffect(() => {
		void flags.showFlag(flagDescriptor);
	}, [flagDescriptor, flags]);

	return null;
};
